import { Flex, Heading, Icon, VStack } from '@chakra-ui/react';
import {
  ChevronDownIcon,
  FileCheck2Icon,
  FileIcon,
  FolderIcon,
} from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { ChecklistItem } from '../utils/buildChecklistItems';
import { groupItemsByFolder } from '../utils/groupItemsByFolder';
import { ChecklistItemRow } from './ChecklistItemRow';

interface OtherDocumentsListProps {
  items: ChecklistItem[];
  /** The active checklist item's id (the item containing the selected tab). */
  activeItemId?: string;
  /**
   * When true, group the items by their platform folder (folder-name
   * sub-headers) instead of a flat list. Used on non-SkySlope (Dotloop,
   * ZipForm) surfaces; SkySlope keeps the flat checklist-item list.
   */
  groupByFolder?: boolean;
  /** Selects a checklist item by opening its first document tab. */
  onSelectDocument: (ingestionDocumentId: string) => void;
}

/** The flat (SkySlope) list of checklist-item rows. */
const FlatItems: FC<{
  items: ChecklistItem[];
  activeItemId?: string;
  onSelectDocument: (ingestionDocumentId: string) => void;
}> = ({ items, activeItemId, onSelectDocument }) => (
  <VStack align="stretch" id="other-documents-panel" spacing={1.5}>
    {items.map((item) => (
      <ChecklistItemRow
        key={item.id}
        // Standalone documents (not attached to any SkySlope checklist
        // requirement) get a plain-file icon + "Not on checklist" caption to
        // set them apart from auto-approved checklist items.
        caption={item.isChecklistItem ? undefined : 'Not on checklist'}
        icon={item.isChecklistItem ? FileCheck2Icon : FileIcon}
        isSelected={item.id === activeItemId}
        title={item.title}
        onSelect={() => onSelectDocument(item.docs[0].ingestionDocumentId)}
      />
    ))}
  </VStack>
);

/** The folder-grouped (Dotloop / ZipForm) list: a folder-name header per group. */
const FolderGroupedItems: FC<{
  items: ChecklistItem[];
  activeItemId?: string;
  onSelectDocument: (ingestionDocumentId: string) => void;
}> = ({ items, activeItemId, onSelectDocument }) => {
  const folderGroups = useMemo(() => groupItemsByFolder(items), [items]);

  return (
    <VStack align="stretch" id="other-documents-panel" spacing={4}>
      {folderGroups.map((group) => (
        <VStack key={group.folderId} align="stretch" spacing={1.5}>
          <Flex align="center" gap={2} px={2.5}>
            <Icon
              as={FolderIcon}
              boxSize="14px"
              color="compliance.text.muted"
              flexShrink={0}
            />
            <Heading
              color="compliance.text.secondary"
              fontSize="sm"
              fontWeight="medium"
              overflow="hidden"
              size="headline"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {group.folderName}
            </Heading>
          </Flex>
          {group.items.map((item) => (
            <ChecklistItemRow
              key={item.id}
              isSelected={item.id === activeItemId}
              title={item.title}
              onSelect={() =>
                onSelectDocument(item.docs[0].ingestionDocumentId)
              }
            />
          ))}
        </VStack>
      ))}
    </VStack>
  );
};

/**
 * Left-rail "Other documents" section: the items that don't need review
 * (auto-approved / already handled). Collapsible and collapsed by default so
 * the rail leads with the Needs-Review work. Flat for SkySlope; grouped by
 * platform folder for non-SkySlope (Dotloop, ZipForm) via
 * {@link OtherDocumentsListProps.groupByFolder}. Renders nothing when empty.
 */
export const OtherDocumentsList: FC<OtherDocumentsListProps> = ({
  items,
  activeItemId,
  groupByFolder = false,
  onSelectDocument,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) {
    return null;
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Flex
        align="center"
        aria-controls="other-documents-panel"
        aria-expanded={expanded}
        cursor="pointer"
        justify="space-between"
        px={2.5}
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((previous) => !previous)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((previous) => !previous);
          }
        }}
      >
        <Heading
          color="compliance.text.secondary"
          fontSize="md"
          fontWeight="semibold"
          size="headline"
        >
          Other documents
        </Heading>
        <Icon
          as={ChevronDownIcon}
          boxSize="18px"
          color="compliance.text.secondary"
          flexShrink={0}
          transform={expanded ? undefined : 'rotate(-90deg)'}
          transition="transform 0.15s ease"
        />
      </Flex>

      {expanded &&
        (groupByFolder ? (
          <FolderGroupedItems
            activeItemId={activeItemId}
            items={items}
            onSelectDocument={onSelectDocument}
          />
        ) : (
          <FlatItems
            activeItemId={activeItemId}
            items={items}
            onSelectDocument={onSelectDocument}
          />
        ))}
    </VStack>
  );
};
